import { CheckCircle, Circle, Loader } from "lucide-react";
import type { PhaseInfo, Phase } from "@/types/api.types";

interface PhaseTimelineProps {
  phases: PhaseInfo[];
  currentPhase?: Phase;
}

const formatPhase = (phase: string) =>
  phase.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function PhaseTimeline({
  phases,
  currentPhase,
}: PhaseTimelineProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <h3 className="font-semibold text-gray-800 mb-6">Tournament Timeline</h3>

      <div className="flex items-center justify-between overflow-x-auto pb-2">
        {phases.map((phaseInfo, index) => {
          const isCurrent = phaseInfo.phase === currentPhase;
          const isPast = phaseInfo.isCompleted;
          const hasMatches = phaseInfo.totalMatches > 0;

          return (
            <div key={phaseInfo.phase} className="flex items-center">
              {/* Phase Node */}
              <div className="flex flex-col items-center min-w-[90px]">
                {/* Icon */}
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    isPast
                      ? "bg-green-500 border-green-500"
                      : isCurrent
                        ? "bg-blue-500 border-blue-500"
                        : hasMatches
                          ? "bg-orange-100 border-orange-400"
                          : "bg-gray-100 border-gray-300"
                  }`}
                >
                  {isPast ? (
                    <CheckCircle className="h-5 w-5 text-white" />
                  ) : isCurrent && phaseInfo.isActive ? (
                    <Loader className="h-5 w-5 text-white animate-spin" />
                  ) : isCurrent ? (
                    <Circle className="h-5 w-5 text-white fill-white" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-400" />
                  )}
                </div>

                {/* Label */}
                <p
                  className={`text-xs font-medium mt-2 text-center ${
                    isCurrent
                      ? "text-blue-600"
                      : isPast
                        ? "text-green-600"
                        : "text-gray-400"
                  }`}
                >
                  {formatPhase(phaseInfo.phase)}
                </p>

                {/* Match count */}
                {hasMatches && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {phaseInfo.completedMatches}/{phaseInfo.totalMatches}
                  </p>
                )}
              </div>

              {/* Connector line */}
              {index < phases.length - 1 && (
                <div
                  className={`h-0.5 w-8 mx-1 ${
                    isPast ? "bg-green-400" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
